import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Wrench, Loader2, CheckCircle2, Send } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Stars } from '../components/fitting/CentreRating';
import { fittingAPI } from '../services/api';
import { useT } from '../services/i18n';


// Public page (link from the review-request e-mail): rate the partner centre after the mounting appointment
export default function FittingReviewPage() {
  const { token } = useParams();
  const { t } = useT();
  const LABELS = ['', t('frev.l1'), t('frev.l2'), t('frev.l3'), t('frev.l4'), t('frev.l5')];
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(Math.min(5, Math.max(0, parseInt(params.get('note'), 10) || 0)));
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { fittingAPI.review(token).then(setData).catch((e) => setError(e.response?.data?.detail || t('frev.invalidLink'))); }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError('');
    try { setData(await fittingAPI.postReview(token, { rating, comment })); setDone(true); } catch (err) { setError(err.response?.data?.detail || t('frev.sendError')); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />
      <main className="flex-1 max-w-xl mx-auto w-full px-4 py-10" data-testid="fitting-review-page">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center gap-2 mb-2"><Wrench className="text-orange-500" /> {t('frev.title')}</h1>
        {error && !data && <p className="text-red-600" data-testid="fr-error">{error}</p>}
        {data && (
          <div className="bg-white rounded-xl shadow-sm p-6 space-y-5">
            <p className="text-sm text-gray-600" data-testid="fr-intro">{t('frev.intro', { name: data.customer_first_name, n: data.tyres })} <strong>{data.centre.name}</strong> ({data.centre.city}){data.appointment?.date_label ? ` ${t('frev.on')} ${data.appointment.date_label}` : ''} — {t('frev.order')} <strong>{data.order_number}</strong>.</p>
            {data.review ? (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="fr-done">
                <p className="font-semibold flex items-center gap-2"><CheckCircle2 size={18} /> {done ? t('frev.thanks') : t('frev.already')}</p>
                <p className="mt-2 flex items-center gap-2"><Stars value={data.review.rating} size={16} testId="fr-given-stars" /> <span>{LABELS[data.review.rating]}</span></p>
                {data.review.comment && <p className="mt-1 text-gray-700 italic">« {data.review.comment} »</p>}
                {data.review.reply && <div className="mt-3 rounded-md border border-orange-200 bg-orange-50 px-3 py-2 text-gray-800" data-testid="fr-reply"><p className="text-xs font-semibold text-orange-800">{t('frev.replyFrom', { author: data.review.reply.author })}</p><p className="mt-0.5 text-sm">{data.review.reply.message}</p></div>}
                <Link to="/" className="inline-block mt-3 text-xs underline">{t('frev.backShop')}</Link>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-800 mb-2">{t('frev.question')}</p>
                  <div className="flex items-center gap-3">
                    <Stars value={rating} size={32} onChange={setRating} testId="fr-star" />
                    <span className="text-sm text-gray-600" data-testid="fr-rating-label">{LABELS[rating] || t('frev.clickStar')}</span>
                  </div>
                </div>
                <label className="block text-sm font-medium text-gray-800">{t('frev.comment')}
                  <textarea value={comment} onChange={(e) => setComment(e.target.value.slice(0, 1000))} rows={4} placeholder={t('frev.commentPh')} className="mt-1 w-full rounded-md border border-gray-300 p-2 text-sm" data-testid="fr-comment" />
                  <span className="block text-right text-[11px] text-gray-400">{comment.length}/1000</span>
                </label>
                {error && <p className="text-sm text-red-600" data-testid="fr-error">{error}</p>}
                <Button type="submit" disabled={busy || !rating} className="bg-orange-500 hover:bg-orange-600 h-11 px-6" data-testid="fr-submit">{busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />} {t('frev.submit')}</Button>
              </form>
            )}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
